import { client as axios } from '../../axios/client';
import {
  CREATE_TICKET_REQUEST,
  CREATE_TICKET_SUCCESS,
  CREATE_TICKET_FAILURE,
  GET_TICKETS_REQUEST,
  GET_TICKETS_SUCCESS,
  GET_TICKETS_FAILURE,
  UPDATE_COLUMN_SUCCESS,
  UPDATE_TICKET_REQUEST,
  UPDATE_TICKET_SUCCESS,
  UPDATE_TICKET_FAILURE,
  SET_DATE_FILTERS,
  CLEAR_DATE_FILTERS,
  SET_COLUMN_LOADING_MORE,
  LOAD_PAGE_TICKETS_SUCCESS,
  SET_COLUMN_PAGE,
} from './kanbanTypes';
import { INVALIDATE_TICKET_HISTORY } from './historyTypes';
import { logger} from './utils/kanbanUtils';

// Función interna para procesar tickets
const processTickets = async (tickets) => {
  return tickets;
};

/**
 * Obtiene tickets filtrados por fecha y agente
 * @param {Object} filters - Filtros para los tickets
 * @param {string} filters.startDate - Fecha inicial (formato YYYY-MM-DD)
 * @param {string} filters.endDate - Fecha final (formato YYYY-MM-DD)
 * @param {string} filters.assignedAgent - ID del agente asignado
 * @param {string} token - Token de autenticación
 * @returns {Promise<Object>} - Promesa que resuelve a un objeto con los tickets filtrados por columna
 */
// Función interna - no se exporta porque solo se usa dentro de getTicketsByColumn
const getFilteredTickets = (filters, token) => async (dispatch) => {
  try {
    dispatch({ type: GET_TICKETS_REQUEST });

    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = {};

    if (filters.startDate) {
      params.start_date = filters.startDate;
    }
    if (filters.endDate) {
      params.end_date = filters.endDate;
    }
    if (filters.assignedAgent) {
      params.assigned_to_agent = filters.assignedAgent;
    }

    // Si solo hay fecha final pero no inicial, usar la misma fecha como inicial
    if (!filters.startDate && filters.endDate) {
      params.start_date = filters.endDate;
    }

    // Verificar que los parámetros de fecha sean válidos
    if (params.start_date && params.end_date) {
      const startDate = new Date(params.start_date);
      const endDate = new Date(params.end_date);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        logger.error('Fechas inválidas', { start_date: params.start_date, end_date: params.end_date });
      } else if (startDate > endDate) {
        const temp = params.start_date;
        params.start_date = params.end_date;
        params.end_date = temp;
      }
    }

    const response = await axios.get('/kanban/tickets', {
      headers,
      params
    });

    // Verificar si la respuesta contiene tickets
    const tickets = Array.isArray(response.data) ? response.data :
                   (response.data.items && Array.isArray(response.data.items) ? response.data.items : []);

    if (tickets.length === 0) {
      dispatch({
        type: GET_TICKETS_FAILURE,
        payload: { error: 'No se encontraron tickets para los filtros aplicados' }
      });
      return {};
    }

    // Agrupar los tickets por columna
    const ticketsByColumn = {};

    for (const ticket of tickets) {
      const columnId = ticket.column_id;

      if (!ticketsByColumn[columnId]) {
        ticketsByColumn[columnId] = [];
      }

      ticketsByColumn[columnId].push(ticket);
    }

    // Actualizar el estado con los tickets filtrados
    for (const columnId in ticketsByColumn) {
      const columnTickets = ticketsByColumn[columnId];
      const processedTickets = await processTickets(columnTickets);

      dispatch({
        type: GET_TICKETS_SUCCESS,
        payload: {
          columnId,
          tickets: processedTickets
        }
      });
    }

    return ticketsByColumn;
  } catch (error) {
    logger.error('Error al obtener tickets filtrados', error);
    dispatch({
      type: GET_TICKETS_FAILURE,
      payload: { error: error.message || 'Error al obtener tickets filtrados' }
    });
    return {};
  }
};

/**
 * Establece los filtros para los tickets (fecha y agente)
 * @param {Object} filters - Filtros para tickets
 * @param {string} filters.startDate - Fecha inicial (formato YYYY-MM-DD)
 * @param {string} filters.endDate - Fecha final (formato YYYY-MM-DD)
 * @param {string} filters.assignedAgent - ID del agente asignado
 */
export const setDateFilters = (filters) => (dispatch) => {
  if (!filters.startDate && !filters.endDate && !filters.assignedAgent) {
    return;
  }

  const formattedFilters = {
    startDate: filters.startDate ? filters.startDate.trim() : null,
    endDate: filters.endDate ? filters.endDate.trim() : null,
    assignedAgent: filters.assignedAgent ? filters.assignedAgent.trim() : null
  };

  // Validar el formato de las fechas (YYYY-MM-DD)
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

  if (formattedFilters.startDate && !dateRegex.test(formattedFilters.startDate)) {
    logger.error(`Formato de fecha inicial inválido: ${formattedFilters.startDate}. Debe ser YYYY-MM-DD`);
    return;
  }

  if (formattedFilters.endDate && !dateRegex.test(formattedFilters.endDate)) {
    logger.error(`Formato de fecha final inválido: ${formattedFilters.endDate}. Debe ser YYYY-MM-DD`);
    return;
  }

  // Activar loading antes de aplicar filtros
  dispatch({ type: GET_TICKETS_REQUEST });

  dispatch({
    type: SET_DATE_FILTERS,
    payload: formattedFilters
  });
};

/**
 * Limpia los filtros de fecha para los tickets y fuerza una recarga completa
 * @param {Function} getTokenSilent - Función para obtener el token de autenticación
 * @returns {Promise<void>}
 */
export const clearDateFilters = (getTokenSilent) => async (dispatch, getState) => {
  // Activar loading antes de limpiar filtros
  dispatch({ type: GET_TICKETS_REQUEST });
  
  dispatch({
    type: CLEAR_DATE_FILTERS
  });

  try {
    const state = getState();
    const columns = state.kanban.columns?.items || [];
    
    // Obtener el token usando la función proporcionada
    let token;
    try {
      token = await getTokenSilent();
      if (!token) {
        throw new Error('Token no disponible');
      }
    } catch (tokenError) {
      logger.error('No se pudo obtener el token para recargar tickets', tokenError);
      return;
    }

    // Usar método batch optimizado para recargar todas las columnas
    const columnIds = columns.map(col => col.id).filter(Boolean);
    
    if (columnIds.length > 0) {
      try {
        // Usar el método batch optimizado para recargar todos los tickets sin filtros
        const boardId = columns[0]?.board_id;
        if (boardId) {
          await dispatch(getTicketsByBoardBatchPaginated(
            boardId,
            token,
            null, // Sin filtros para obtener todos los tickets
            { limit: 5, offset: 0 }
          ));
        } else {
          throw new Error('Board ID no disponible para recarga batch');
        }
      } catch (batchError) {
        logger.error('Error al recargar tickets con método batch, usando fallback individual:', batchError);
        
        // FALLBACK: Si el batch falla, usar peticiones individuales
        const reloadPromises = columns.map(async (column) => {
          if (!column || !column.id) return;

          try {
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`/kanban/columns/${column.id}/tickets`, { headers });
            
            let tickets = [];
            if (response.data?.items && Array.isArray(response.data.items)) {
              tickets = response.data.items;
            } else if (Array.isArray(response.data)) {
              tickets = response.data;
            }

            dispatch({
              type: GET_TICKETS_SUCCESS,
              payload: {
                columnId: column.id,
                tickets: tickets
              }
            });

            return { columnId: column.id, tickets };
          } catch (columnError) {
            logger.error(`Error al recargar tickets para columna ${column.id}`, columnError);
            return [];
          }
        });

        await Promise.all(reloadPromises);
      }
    }

  } catch (error) {
    logger.error('Error al recargar tickets después de limpiar filtros', error);
  }
};

/**
 * Crea un nuevo ticket en una columna
 * @param {Object} ticketData - Datos del ticket
 * @param {string} ticketData.column_id - ID de la columna donde se creará el ticket
 * @param {string} ticketData.board_id - ID del tablero al que pertenece el ticket
 * @param {string} ticketData.name - Nombre del ticket
 * @param {string} ticketData.description - Descripción del ticket (opcional)
 * @param {string} ticketData.created_by_agent - ID del usuario que crea el ticket
 * @param {string} ticketData.id_customer - ID del cliente
 * @param {string} ticketData.id_contact - ID del contacto
 * @param {string} ticketData.id_agent - ID del agente (opcional)
 * @param {string} ticketData.creator_agent_name - Nombre del agente creador (opcional)
 * @param {string} ticketData.name_contact - Nombre del contacto (opcional)
 * @param {string} ticketData.assigned_to_agent - ID del usuario asignado (opcional)
 * @param {string} ticketData.assigned_agent_name - Nombre del agente asignado (opcional)
 * @param {string} ticketData.meta_data - Metadatos en formato JSON string (opcional)
 * @param {string} ticketData.token - Token de autenticación
 */
export const createTicket = (ticketData) => async (dispatch, getState) => {
    try {
        dispatch({ type: CREATE_TICKET_REQUEST });

        // Verificar que tenemos los datos necesarios
        if (!ticketData.column_id) {
            throw new Error('Es necesario especificar una columna (column_id)');
        }

        // Obtener el nombre del agente del estado
        const state = getState();
        const currentAgent = state.agent?.currentAgent || {};
        const agentName = currentAgent.display_name || currentAgent.name || '';

        // Verificar si ya tenemos el nombre de los agentes
        let creatorName = ticketData.creator_agent_name;
        if (!creatorName || creatorName.trim() === '') {
            creatorName = agentName;
        }

        let assignedName = ticketData.assigned_agent_name;
        if (!assignedName || assignedName.trim() === '') {
            // Si hay un assigned_to_agent, intentar buscar su nombre
            if (ticketData.assigned_to_agent) {
                const agents = state.agent?.agents?.items || [];
                const assignedAgent = agents.find(a => a._id === ticketData.assigned_to_agent);
                if (assignedAgent) {
                    assignedName = assignedAgent.name || assignedAgent.display_name || '';
                }
            }

            // Si aún no tenemos nombre, usar el del creador
            if (!assignedName || assignedName.trim() === '') {
                assignedName = creatorName;
            }
        }

        // Preparar los parámetros para enviar como query params
        const params = {
            name: ticketData.name,
            description: ticketData.description || '',
            created_by_agent: ticketData.created_by_agent,
            creator_agent_name: creatorName,
            id_customer: ticketData.id_customer,
            id_contact: ticketData.id_contact,
            name_contact: ticketData.name_contact,
            assigned_to_agent: ticketData.assigned_to_agent || ticketData.created_by_agent,
            assigned_agent_name: assignedName,
            meta_data: typeof ticketData.meta_data === 'string' ? ticketData.meta_data : JSON.stringify(ticketData.meta_data),
            board_id: ticketData.board_id 
        };

        // Limpiar parámetros undefined para no enviarlos
        Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

        // Eliminar explícitamente status y position si existen
        delete params.status;
        delete params.position;

        // Asegurarse de que tenemos el token
        const token = ticketData.token;
        if (!token) {
            throw new Error('Token de autenticación no proporcionado');
        }

        try {
            // Configurar los headers con el token
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            // URL del endpoint
            const url = `/kanban/columns/${ticketData.column_id}/tickets`;

            // Realizar la petición POST enviando los datos como parámetros de consulta
            const response = await axios.post(
                url,
                null, 
                { headers, params } 
            );

            // Formatear el ticket recibido para mantener coherencia en el estado
            const formattedTicket = {
                id: response.data.id || response.data._id,
                name: response.data.name || ticketData.name,
                description: response.data.description || ticketData.description || '',
                meta_data: response.data.meta_data || params.meta_data || '{}',
                created_by_agent: response.data.created_by_agent || ticketData.created_by_agent,
                id_customer: response.data.id_customer || ticketData.id_customer,
                id_contact: response.data.id_contact || ticketData.id_contact,
                assigned_to_agent: response.data.assigned_to_agent ? String(response.data.assigned_to_agent) : ticketData.assigned_to_agent,
                column_id: ticketData.column_id,
                board_id: response.data.board_id || ticketData.board_id,
                created_at: response.data.created_at || new Date().toISOString(),
                contact_name: response.data.name_contact || ticketData.name_contact || '',
                assigned_name: response.data.assigned_agent_name || assignedName,
                creator_agent_name: response.data.creator_agent_name || creatorName
            };

            // Actualizar el estado de Redux
            dispatch({
                type: CREATE_TICKET_SUCCESS,
                payload: formattedTicket
            });

            // Después de la creación exitosa, recargar la columna donde se creó el ticket
            try {
                await dispatch(getTicketsByColumn(formattedTicket.column_id, token));
            } catch (reloadError) {
                console.error('Error al recargar la columna después de crear ticket:', reloadError);
            }

            return formattedTicket;
        } catch (error) {
            if (error.response) {
                logger.error('Error en la petición de creación de ticket:', {
                    status: error.response.status,
                    data: error.response.data
                });
            }

            throw new Error(error.message || 'Error al crear el ticket');
        }
    } catch (error) {
        dispatch({
            type: CREATE_TICKET_FAILURE,
            payload: {
                error: error.message || 'Error al crear el ticket'
            }
        });
        throw error;
    }
};

/**
 * Obtiene los tickets de una columna específica
 * @param {string} columnId - ID de la columna
 * @param {string} token - Token de autenticación (opcional)
 * @param {Object} filters - Filtros para tickets (opcional)
 * @param {string} filters.startDate - Fecha inicial (formato YYYY-MM-DD)
 * @param {string} filters.endDate - Fecha final (formato YYYY-MM-DD)
 * @param {string} filters.assignedAgent - ID del agente asignado
 * @returns {Promise<Array>} Array de tickets
 */
export const getTicketsByColumn = (columnId, token, filters) => async (dispatch, getState) => {
  try {
    // Verificar que tenemos los datos necesarios
    if (!columnId) {
      throw new Error('Es necesario especificar una columna (columnId)');
    }

    // Si no se proporciona token, intentar obtenerlo del estado de Redux
    let authToken = token;
    if (!authToken) {
      const state = getState();
      authToken = state.auth?.token;
      if (!authToken) {
        throw new Error('No se pudo obtener el token de autenticación');
      }
    }

    // Verificar si ya tenemos tickets para esta columna en el estado
    const state = getState();
    const columns = state.kanban.columns?.items || [];
    const columnTickets = state.kanban.tickets?.columnTickets || {};

    // Buscar la columna específica
    const targetColumn = columns.find(col => String(col.id) === String(columnId));

    // Verificar si hay filtros activos
    const hasActiveFilters = filters && (filters.startDate || filters.endDate || filters.assignedAgent);

    // Si hay filtros activos, usar la función getFilteredTickets
    if (hasActiveFilters) {
      const ticketsByColumn = await dispatch(getFilteredTickets(filters, authToken));

      // Verificar si hay tickets para esta columna
      if (ticketsByColumn && ticketsByColumn[columnId]) {
        return ticketsByColumn[columnId];
      }

      return [];
    }

    // Si no hay filtros activos, verificar si ya tenemos tickets para esta columna
    if (targetColumn && targetColumn.tickets && targetColumn.tickets.length > 0) {
      return targetColumn.tickets;
    }

    // Verificar si tenemos tickets en el mapa de columnTickets
    if (columnTickets[columnId] && columnTickets[columnId].length > 0) {

      // Actualizar la columna con los tickets del cache
      if (targetColumn) {
        const updatedColumn = {
          ...targetColumn,
          tickets: columnTickets[columnId]
        };

        // Actualizar la columna en el estado sin hacer petición a la API
        dispatch({
          type: UPDATE_COLUMN_SUCCESS,
          payload: updatedColumn
        });
      }

      return columnTickets[columnId];
    }

    // Si no tenemos tickets en el estado, hacer la petición a la API
    dispatch({ type: GET_TICKETS_REQUEST });

    // Configurar headers con el token si está disponible
    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

    let response;
    try {
      response = await axios.get(`/kanban/columns/${columnId}/tickets`, {
        headers
      });

      // Procesar la respuesta
    } catch (apiError) {
      logger.error(`Error al obtener tickets para la columna ${columnId}`, apiError);
      throw new Error(`Error al obtener tickets: ${apiError.message}`);
    }

    // Verificar si la respuesta es exitosa
    if (!response.data) {
      throw new Error('No se recibieron datos del servidor');
    }

    // Extraer los tickets de la respuesta
    let tickets = [];

    if (response.data.items && Array.isArray(response.data.items)) {
      tickets = response.data.items;
    } else if (Array.isArray(response.data)) {
      tickets = response.data;
    } else {
      logger.warn('Formato de respuesta inesperado', response.data);
      tickets = [];
    }

    // Verificar si hay tickets optimistas en otras columnas para evitar duplicación
    const optimisticTicketIds = new Set();

    // Buscar en todas las columnas tickets con la propiedad _optimistic
    Object.values(state.kanban.tickets?.columnTickets || {}).forEach(colTickets => {
      colTickets.forEach(ticket => {
        if (ticket && ticket._optimistic) {
          optimisticTicketIds.add(String(ticket.id));
        }
      });
    });

    // Filtrar tickets que ya están marcados como optimistas en otras columnas
    const filteredTickets = tickets.filter(ticket => !optimisticTicketIds.has(String(ticket.id)));

    // Procesar los tickets con la función interna
    const processedTickets = await processTickets(filteredTickets);

    // Actualizar el estado de Redux
    dispatch({
      type: GET_TICKETS_SUCCESS,
      payload: {
        columnId: columnId,
        tickets: processedTickets
      }
    });

    // Actualizar también la columna en el estado
    try {
      // Buscar la columna específica nuevamente por si ha cambiado
      const updatedState = getState();
      // Adaptación a la nueva estructura del estado después de la refactorización
      const updatedColumns = updatedState.kanban.columns?.items || [];
      const updatedTargetColumn = updatedColumns.find(col => String(col.id) === String(columnId));

      if (updatedTargetColumn) {
        // Crear una copia actualizada de la columna con los nuevos tickets
        const updatedColumn = {
          ...updatedTargetColumn,
          tickets: processedTickets
        };

        // Actualizar la columna en el estado
        dispatch({
          type: UPDATE_COLUMN_SUCCESS,
          payload: updatedColumn
        });
      }
    } catch (columnUpdateError) {
      logger.error('Error al actualizar la columna con los tickets', columnUpdateError);
    }

    // Devolver los tickets procesados
    return processedTickets;
  } catch (error) {
    // Manejar errores específicos
    let errorMessage = 'Error al obtener tickets';

    if (error.response) {
      // Error de la API con respuesta
      const status = error.response.status;
      const data = error.response.data;

      if (status === 401) {
        errorMessage = 'No autorizado: verifique sus credenciales';
      } else if (status === 404) {
        errorMessage = 'Columna no encontrada';
      } else if (data && data.message) {
        errorMessage = data.message;
      }
    } else if (error.message) {
      // Error con mensaje personalizado
      errorMessage = error.message;
    }

    logger.error(`Error al obtener tickets para columna ${columnId}`, errorMessage);

    dispatch({
      type: GET_TICKETS_FAILURE,
      payload: {
        columnId: columnId,
        error: errorMessage
      }
    });

    throw new Error(errorMessage);
  }
};

/**
 * Carga más tickets de forma progresiva
/**
 * Actualiza un ticket existente (por ejemplo, cuando se mueve a otra columna)
 * @param {string} ticketId - ID del ticket a actualizar
 * @param {Object} updateData - Datos a actualizar
 * @param {string} updateData.name - Nombre del ticket (opcional)
 * @param {string} updateData.description - Descripción del ticket (opcional)
 * @param {string} updateData.column_id - ID de la nueva columna (opcional)
 * @param {string} updateData.id_agent - ID del agente (opcional)
 * @param {string} updateData.name_agent - Nombre del agente (opcional)
 * @param {string} updateData.name_contact - Nombre del contacto (opcional)
 * @param {string} updateData.assigned_to - ID del usuario asignado (opcional)
 * @param {string} updateData.meta_data - Metadatos en formato JSON string (opcional)
 * @param {string} updateData.token - Token de autenticación
 * @returns {Promise<Object>} Ticket actualizado
 */
export const updateTicket = (ticketId, updateData, token) => async (dispatch, getState) => {
  try {
    dispatch({ type: UPDATE_TICKET_REQUEST });

    // Validación básica
    if (!ticketId) {
      throw new Error('Es necesario especificar el ID del ticket');
    }

    if (!token) {
      throw new Error('Token de autenticación requerido');
    }

    // Configurar los headers con el token
    const headers = {
      'Authorization': `Bearer ${token}`,
    };

    // Preparar datos sin incluir token en params
    const params = {
      ...updateData,
      column_id: updateData.column_id, 
      name: updateData.name,
      description: updateData.description,
      assigned_to_agent: updateData.assigned_to_agent,
      assigned_agent_name: updateData.assigned_agent_name,
      name_contact: updateData.name_contact,
      meta_data: updateData.meta_data,
    };

     // Limpiar parámetros undefined para no enviarlos en la URL
     Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

     // Eliminar token y position de params
     delete params.token;
     delete params.position;

    // Obtener el ticket actual del estado para el manejo optimista
    const state = getState();
    const currentTicket = state.kanban.tickets?.items?.find(
      ticket => String(ticket.id) === String(ticketId)
    );

    // Si no encontramos el ticket en el estado global, intenta buscarlo en columnTickets
    let foundTicket = currentTicket;
    if (!foundTicket) {
      const columnTickets = state.kanban.tickets?.columnTickets || {};
      for (const colId in columnTickets) {
        const ticket = columnTickets[colId]?.find(
          t => String(t.id) === String(ticketId)
        );
        if (ticket) {
          foundTicket = ticket;
          break;
        }
      }
    }

    // Si aún no lo encontramos, creamos un ticket base con el ID
    if (!foundTicket) {
      foundTicket = { id: ticketId };
    }

    try {
      // Realizar la solicitud PUT enviando los datos como parámetros de consulta
      const response = await axios.put(
        `/kanban/tickets/${ticketId}`,
        null, // El body es null cuando enviamos solo query params
        { headers, params } // Pasar los parámetros de consulta en el objeto de configuración
      );

      // Procesar el ticket actualizado
      const updatedTicket = {
        ...response.data,
        // Usar el column_id de la respuesta si está presente, si no, usar el que intentamos establecer
        column_id: response.data?.column_id || updateData.column_id
      };

      // Actualizar el estado global con el ticket actualizado
      dispatch({
        type: UPDATE_TICKET_SUCCESS,
        payload: {
          ticketId,
          updatedTicket
        }
      });

      // Si la actualización incluyó un cambio de columna, invalidamos el historial.
      if (updateData.column_id) {
          dispatch({
              type: INVALIDATE_TICKET_HISTORY,
              payload: { ticketId }
          });
      }

      // Manejar la actualización de las columnas como antes...
      handleColumnsUpdate(dispatch, getState, ticketId, updateData, token);

      return updatedTicket;
    } catch (apiError) {
      logger.error(`Error al actualizar el ticket ${ticketId}:`, apiError);

      if (apiError.response) {
        logger.error('Detalles del error de actualización:', {
          status: apiError.response.status,
          data: apiError.response.data
        });
      }

      // Si es un error 500, implementar manejo optimista
      if (apiError.response && apiError.response.status === 500) {
        logger.warn("Error 500 al actualizar. Implementando manejo optimista para mantener la UI funcional.");

        // Crear un ticket actualizado basado en los datos que intentamos enviar
        const optimisticTicket = {
          ...foundTicket,
          ...updateData,
          // Marcar el ticket para que sepamos que es un cambio optimista
          _optimistic: true
        };

        // Actualizar el estado con nuestro ticket optimista
        dispatch({
          type: UPDATE_TICKET_SUCCESS,
          payload: {
            ticketId,
            updatedTicket: optimisticTicket
          }
        });

        // Intentar recargar las columnas para que eventualmente tengamos datos correctos
        try {
          handleColumnsUpdate(dispatch, getState, ticketId, updateData, token);
        } catch (refreshError) {
          logger.warn('Error al recargar columnas después de actualización optimista:', refreshError);
        }

        // No propagamos el error para que la UI siga funcionando
        return optimisticTicket;
      }

      // Para otros errores, sí propagamos el error
      dispatch({
        type: UPDATE_TICKET_FAILURE,
        payload: {
          error: apiError.message || 'Error al actualizar el ticket'
        }
      });

      throw apiError;
    }
  } catch (error) {
    console.error(`Error al procesar la actualización del ticket ${ticketId}:`, error);

    dispatch({
      type: UPDATE_TICKET_FAILURE,
      payload: {
        error: error.message || 'Error al actualizar el ticket'
      }
    });

    throw error;
  }
};

// Función auxiliar para manejar la actualización de columnas después de mover un ticket
const handleColumnsUpdate = async (dispatch, getState, ticketId, updateData, token) => {
  // Solo proceder si hay cambio de columna
  // Si no hay column_id en updateData, significa que no se está moviendo de columna
  if (!updateData.column_id) {
    return;
  }

  const state = getState();
  const columnTickets = state.kanban.tickets?.columnTickets || {};

  let sourceColumnId = null;

  // Buscar la columna de origen ANTES de la actualización en el estado local
  for (const [colId, tickets] of Object.entries(columnTickets)) {
    if (tickets && Array.isArray(tickets) && tickets.some(ticket => String(ticket.id) === String(ticketId))) {
      sourceColumnId = colId;
      break;
    }
  }

  const targetColumnId = String(updateData.column_id);

  // Solo recargamos las columnas si el ticket realmente cambió de columna
  // O si la columna de origen no pudo ser determinada (para asegurar consistencia)
  if ((sourceColumnId && targetColumnId && sourceColumnId !== targetColumnId) || (!sourceColumnId && targetColumnId)) {
    try {
      // Dar un poco de tiempo al servidor para procesar antes de recargar
      await new Promise(resolve => setTimeout(resolve, 500));

      // Obtener el estado actual antes de recargar
      const preReloadState = getState();
      const hasOptimisticUpdate = preReloadState.kanban.tickets?.columnTickets?.[targetColumnId]?.some(
        ticket => ticket._optimistic && String(ticket.id) === String(ticketId)
      );

      // Si ya tenemos una actualización optimista y es un error 500, no recargamos para evitar duplicación
      if (hasOptimisticUpdate) {
        // Enviar una acción para marcar el ticket como verificado para evitar duplicaciones
        dispatch({
          type: 'MARK_TICKET_VERIFIED',
          payload: {
            ticketId,
            columnId: targetColumnId
          }
        });

        return;
      }

      // Proceder con la recarga normal
      await Promise.all([
        // Recargar la columna de origen solo si la conocemos
        sourceColumnId ? dispatch(getTicketsByColumn(sourceColumnId, token, preReloadState.kanban.tickets?.filters)) : Promise.resolve(),
        dispatch(getTicketsByColumn(targetColumnId, token, preReloadState.kanban.tickets?.filters))
      ]);

    } catch (refreshError) {
      logger.warn('Error al recargar tickets de columnas después de mover:', refreshError);
    }
  } else if (targetColumnId && sourceColumnId === targetColumnId) {
    // Si el ticket se actualiza pero permanece en la misma columna (ej: cambio de posición/datos)
    try {
      // Dar un poco de tiempo al servidor para procesar antes de recargar
      await new Promise(resolve => setTimeout(resolve, 500));

      // Verificar si ya existe una actualización optimista
      const preReloadState = getState();
      const hasOptimisticUpdate = preReloadState.kanban.tickets?.columnTickets?.[targetColumnId]?.some(
        ticket => ticket._optimistic && String(ticket.id) === String(ticketId)
      );

      // Si existe actualización optimista, no recargamos
      if (hasOptimisticUpdate) {
        return;
      }

      await dispatch(getTicketsByColumn(targetColumnId, token, preReloadState.kanban.tickets?.filters));
    } catch (refreshError) {
      logger.warn('Error al actualizar tickets de columna (cambio de posición):', refreshError);
    }
  }
};


/**
 * Obtiene tickets de múltiples columnas con paginación en una sola petición batch optimizada

 * Reemplazada completamente por getTicketsByBoardBatchPaginated (más eficiente)
 */

/**
 * Obtiene tickets de todas las columnas de un board con paginación optimizada usando board_id
 * @param {number} boardId - ID del board/tablero
 * @param {string} token - Token de autenticación
 * @param {Object} filters - Filtros opcionales (fecha y agente)
 * @param {Object} pagination - Paginación opcional (limit y offset) - por defecto limit=5, offset=0
 * @returns {Promise<Object>} - Promesa que resuelve a un objeto con tickets agrupados por columna
 */
export const getTicketsByBoardBatchPaginated = (boardId, token, filters, pagination) => async (dispatch, getState) => {
  try {
    // Verificar que tenemos el boardId
    if (!boardId) {
      const error = 'Se requiere un ID de board válido';
      throw new Error(error);
    }

    // Si no se proporciona token, intentar obtenerlo del estado de Redux
    let authToken = token;
    if (!authToken) {
      const state = getState();
      authToken = state.auth?.token;
      if (!authToken) {
        const error = 'No se pudo obtener el token de autenticación';
        throw new Error(error);
      }
    }

    // Asegurar formato del token
    const headers = authToken ? { 
      Authorization: `Bearer ${authToken.replace('Bearer ', '')}`,
      'Content-Type': 'application/json'
    } : {
      'Content-Type': 'application/json'
    };

    // Preparar el cuerpo de la petición usando board_id (más eficiente)
    const requestBody = {
      board_id: boardId
    };

    // Agregar filtros si existen
    if (filters && (filters.startDate || filters.endDate || filters.assignedAgent)) {
      requestBody.filters = {};
      
      if (filters.startDate) {
        requestBody.filters.start_date = filters.startDate;
      }
      
      if (filters.endDate) {
        requestBody.filters.end_date = filters.endDate;
      }
      
      if (filters.assignedAgent) {
        requestBody.filters.assigned_to_agent = filters.assignedAgent;
      }
    }

    // Agregar paginación si existe (si no se especifica, el backend usa limit=5, offset=0 por defecto)
    if (pagination && (pagination.limit !== undefined || pagination.offset !== undefined)) {
      requestBody.pagination = {};
      
      if (pagination.limit !== undefined) {
        requestBody.pagination.limit = pagination.limit;
      }
      
      if (pagination.offset !== undefined) {
        requestBody.pagination.offset = pagination.offset;
      }
    }

    dispatch({ type: GET_TICKETS_REQUEST });

    // Realizar la petición batch optimizada con board_id
    const response = await axios.post('/kanban/tickets/batch', requestBody, { headers });

    // Verificar la respuesta
    if (!response.data) {
      const error = 'No se recibieron datos del servidor';
      throw new Error(error);
    }

    const ticketsByColumn = response.data;

    // Procesar y actualizar el estado para cada columna
    const processedResults = {};
    
    for (const columnIdStr in ticketsByColumn) {
      const columnId = columnIdStr; // Mantener como string para consistencia
      const tickets = ticketsByColumn[columnIdStr] || [];
      
      // Procesar los tickets
      const processedTickets = await processTickets(tickets);
      
      // Si es la primera página (offset = 0), reemplazar tickets
      // Si no, agregar a los existentes (para paginación)
      const shouldAppend = pagination && pagination.offset > 0;
      
      if (shouldAppend) {
        // Solo enviar los nuevos tickets - el reducer se encarga de combinarlos
        dispatch({
          type: 'APPEND_TICKETS_SUCCESS',
          payload: {
            columnId: columnId,
            tickets: processedTickets  // Solo los nuevos tickets
          }
        });
      } else {
        // Reemplazar tickets (primera carga)
        dispatch({
          type: GET_TICKETS_SUCCESS,
          payload: {
            columnId: columnId,
            tickets: processedTickets
          }
        });
      }

      // Actualizar también la columna en el estado solo si no es append
      // (si es append, el reducer ya maneja la combinación)
      if (!shouldAppend) {
        const state = getState();
        const columns = state.kanban.columns?.items || [];
        const targetColumn = columns.find(col => String(col.id) === String(columnId));

        if (targetColumn) {
          const updatedColumn = {
            ...targetColumn,
            tickets: processedTickets
          };

          dispatch({
            type: UPDATE_COLUMN_SUCCESS,
            payload: updatedColumn
          });
        }
      }

      processedResults[columnId] = processedTickets;
    }
    
    return processedResults;

  } catch (error) {
    // Manejar errores específicos
    let errorMessage = 'Error al obtener tickets por board en lote paginado';

    if (error.response) {
      // Error de la API con respuesta
      const status = error.response.status;
      const data = error.response.data;

      if (status === 401) {
        errorMessage = 'No autorizado: verifique sus credenciales';
      } else if (status === 400) {
        errorMessage = data?.detail || 'Petición inválida';
      } else if (status === 404) {
        errorMessage = 'Board no encontrado o sin columnas';
      } else if (data && data.detail) {
        errorMessage = data.detail;
      }
    } else if (error.message) {
      // Error con mensaje personalizado
      errorMessage = error.message;
    }

    console.error(`Error en petición batch paginada para board ${boardId}:`, errorMessage);

    dispatch({
      type: GET_TICKETS_FAILURE,
      payload: {
        error: errorMessage
      }
    });

    throw new Error(errorMessage);
  }
};

/**
 * Acción para cargar una página específica de tickets para una columna
 * USA EL ENDPOINT EXISTENTE - No requiere cambios backend
 * @param {string} columnId - ID de la columna
 * @param {number} page - Número de página (1-indexed)
 * @param {string} token - Token de autenticación  
 * @param {Object} filters - Filtros opcionales
 * @param {number} ticketsPerPage - Tickets por página (default: 5)
 * @returns {Promise<boolean>} - Promesa que resuelve a true si se cargaron tickets
 */
export const loadColumnPage = (columnId, page, token, filters, ticketsPerPage = 5) => async (dispatch, getState) => {
  try {
    // Verificar que tenemos el columnId y página válida
    if (!columnId || page < 1) {
      throw new Error('Se requiere un ID de columna válido y número de página positivo');
    }

    // Activar loading state
    dispatch({
      type: SET_COLUMN_LOADING_MORE,
      payload: { columnId, loading: true }
    });

    // Calcular offset basado en la página solicitada
    const offset = (page - 1) * ticketsPerPage;

    // Obtener información de la columna y board
    const state = getState();
    const columns = state.kanban.columns?.items || [];
    const column = columns.find(col => String(col.id) === String(columnId));
    
    if (!column || !column.board_id) {
      throw new Error('No se pudo obtener información de la columna');
    }

    // Crear un request específico para esta columna y página
    const requestBody = {
      board_id: column.board_id,
      column_ids: [columnId], // Solo esta columna
      pagination: {
        limit: ticketsPerPage,
        offset: offset
      }
    };

    // Agregar filtros si existen
    if (filters) {
      requestBody.filters = {};
      
      if (filters.startDate) {
        requestBody.filters.start_date = filters.startDate;
      }
      
      if (filters.endDate) {
        requestBody.filters.end_date = filters.endDate;
      }
      
      if (filters.assignedAgent) {
        requestBody.filters.assigned_to_agent = filters.assignedAgent;
      }
    }

    // Llamar al endpoint batch
    const headers = { Authorization: `Bearer ${token}` };
    const response = await axios.post('/kanban/tickets/batch', requestBody, { headers });

    if (!response.data || !response.data[columnId]) {
      throw new Error('No se recibieron datos del servidor para esta columna');
    }

    const tickets = response.data[columnId] || [];
    const processedTickets = await processTickets(tickets);

    // Dispatch con el nuevo action type para páginas
    dispatch({
      type: LOAD_PAGE_TICKETS_SUCCESS,
      payload: {
        columnId: columnId,
        tickets: processedTickets,
        page: page,
        totalEstimated: offset + processedTickets.length // Estimación conservadora
      }
    });

    // Actualizar información de paginación
    dispatch({
      type: SET_COLUMN_PAGE,
      payload: {
        columnId: columnId,
        page: page
      }
    });

    // Desactivar loading state
    dispatch({
      type: SET_COLUMN_LOADING_MORE,
      payload: { columnId, loading: false }
    });

    return processedTickets.length > 0;

  } catch (error) {
    // Desactivar loading state en caso de error
    dispatch({
      type: SET_COLUMN_LOADING_MORE,
      payload: { columnId, loading: false }
    });

    console.error(`Error al cargar página ${page} de columna ${columnId}:`, error);
    throw error;
  }
};

/**
 * Obtiene tickets cerrados (is_active=false) con filtros opcionales
 * @param {number|null} boardId - ID del tablero (opcional)
 * @param {string} token - Token de autenticación
 * @param {Object} filters - Filtros opcionales (start_date, end_date, assigned_to_agent)
 * @param {Object} pagination - Opciones de paginación (offset, limit)
 * @returns {Promise<Object>} - Objeto con tickets y total
 */
export const getClosedTicketsByBoard = (boardId = null, token, filters = {}, pagination = {}) => async (dispatch) => {
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const params = {
      ...filters,
      offset: pagination.offset || 0,
      limit: pagination.limit || 10,
    };

    if (boardId) {
      params.board_id = boardId;
    }

    const response = await axios.get('/kanban/tickets/closed', {
      headers,
      params,
    });

    const tickets = response.data.items || [];
    const total = response.data.count || 0;

    return { tickets, total };

  } catch (error) {
    let detail = 'Error desconocido.';
    if (error.response?.data?.detail) {
      if (Array.isArray(error.response.data.detail)) {
        detail = error.response.data.detail.map(d => `${d.loc.join('.')} - ${d.msg}`).join('; ');
      } else if (typeof error.response.data.detail === 'object') {
        detail = JSON.stringify(error.response.data.detail);
      } else {
        detail = error.response.data.detail;
      }
    } else if (error.message) {
      detail = error.message;
    }
    
    logger.error('Error al obtener tickets cerrados', new Error(detail));
    throw new Error(`Error al obtener tickets cerrados: ${detail}`);
  }
};

